import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { ShieldAlert, Lock, Loader2, X } from 'lucide-react';

/**
 * Step-up authentication gate. Wrap any sensitive view:
 *   <SensitiveGate title="Wallet" onCancel={close}><WalletManagement/></SensitiveGate>
 * Children render only after the backend confirms an active sensitive-access
 * session (or a valid trusted device). Otherwise a verification dialog is shown.
 */
export const SensitiveGate = ({ children = null, title = 'this page', onCancel, onVerified }) => {
  const { user } = useAuth();
  const [state, setState] = useState('loading'); // loading | verified | locked
  const [mfa, setMfa] = useState({ enabled: false, method: null });
  const [mode, setMode] = useState('password'); // password | totp | email
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    apiCall('/api/security/sensitive-status')
      .then((r) => {
        setMfa({ enabled: r.mfa_enabled, method: r.mfa_method });
        setState(r.verified ? 'verified' : 'locked');
        if (r.verified) onVerified?.();
      })
      .catch(() => setState('locked'));
     
  }, []);

  const sendEmailOtp = async () => {
    try {
      await apiCall('/api/auth/otp/send', { method: 'POST', body: JSON.stringify({ email: user?.email }) });
      setOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (e) {
      toast.error(e.message || 'Failed to send code');
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const body = { trust_device: trustDevice };
      if (mode === 'password') body.password = password;
      else if (mode === 'totp') body.totp_code = code;
      else body.email_otp = code;
      await apiCall('/api/security/verify', { method: 'POST', body: JSON.stringify(body) });
      toast.success('Identity verified');
      setState('verified');
      onVerified?.();
    } catch (err) {
      toast.error(err.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'verified') return children;

  if (state === 'loading') {
    return (
      <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4" data-testid="sensitive-gate-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {onCancel && (
          <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
                  data-testid="sensitive-gate-cancel">
            <X className="w-5 h-5" />
          </button>
        )}
        <div className="w-14 h-14 rounded-full bg-cyan-50 flex items-center justify-center mx-auto mb-4">
          <ShieldAlert className="w-7 h-7 text-cyan-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 text-center">Verify your identity</h3>
        <p className="text-sm text-slate-500 text-center mt-1.5">
          For your security, please verify your identity before continuing to {title}.
        </p>

        {mfa.enabled && (
          <div className="flex gap-2 justify-center mt-4">
            {['password', mfa.method === 'totp' ? 'totp' : 'email'].map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${mode === m ? 'bg-cyan-500 text-white' : 'bg-slate-100 text-slate-600'}`}
                      data-testid={`sensitive-mode-${m}`}>
                {m === 'password' ? 'Use password' : m === 'totp' ? 'Authenticator code' : 'Email code'}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 space-y-3">
          {mode === 'password' ? (
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     placeholder="Current password" autoFocus
                     className="w-full h-11 pl-10 pr-4 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-cyan-500"
                     data-testid="sensitive-password-input" />
            </div>
          ) : (
            <>
              {mode === 'email' && !otpSent && (
                <button type="button" onClick={sendEmailOtp}
                        className="w-full h-10 rounded-lg border border-cyan-200 text-cyan-600 text-sm font-medium hover:bg-cyan-50"
                        data-testid="sensitive-send-otp-btn">
                  Send code to my email
                </button>
              )}
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                     placeholder="6-digit code" inputMode="numeric"
                     className="w-full h-11 px-4 border border-slate-200 rounded-lg text-sm text-center tracking-[0.4em] focus:outline-none focus:border-cyan-500"
                     data-testid="sensitive-code-input" />
            </>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)}
                   data-testid="trust-device-checkbox" />
            Trust this device for future access
          </label>

          <button type="submit" disabled={busy || (mode === 'password' ? !password : code.length < 6)}
                  className="w-full h-11 rounded-full bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-40"
                  data-testid="sensitive-verify-btn">
            {busy ? 'Verifying…' : 'Verify & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SensitiveGate;
